//  ==========================================================================
//
//  Image Breaker
//
//  ==========================================================================
import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import Image from '../components/WordsbyImg';
import { css } from 'emotion';
import styled from 'react-emotion';
import { colors, mq } from '../global-styles';

//  ==========================================================================
//  Styles
//  ==========================================================================
const StyledImageBreaker = styled( 'div' )`
	background-color: ${colors.white};
	padding-bottom: 45px;
    margin-bottom: 35px;
    position: relative;
	
	${mq.md(
		css`
            margin-bottom: 70px;
			padding-bottom: 0;
		`
	)};
    ${mq.xl(
		css`
            margin-bottom: 90px;
        `
	)};
`;

const StyledVideoBreaker = styled( 'div' )`
	overflow: hidden;
	max-width: 100vw;
	background-color: ${colors.white};
	padding-bottom: 45px;
    margin-bottom: 35px;
    position: relative;

	${mq.md(
		css`
            margin-bottom: 70px;
			padding-bottom: 0;
		`
	)};
    ${mq.xl(
		css`
            margin-bottom: 90px;
        `
	)};
`;

const StyledVideo = styled( 'video' )`
	position: absolute;
	height: 100%;
    width: 177.77777778vh; /* 100 * 16 / 9 */
    min-width: 100%;
	min-height: 56.25vw; /* 100 * 9 / 16 */
    left: 50%; /* % of surrounding element */
    top: 50%;
    transform: translate(-50%, -50%); /* % of current element */
`;

const ImageWrapper = styled( 'div' )`
	height: 0;
	padding-bottom: 420px;
	position: relative;

	${mq.md(
		css`
			padding-bottom: 670px;
		`
	)};
	${mq.xl(
		css`
			padding-bottom: 750px;
		`
	)};

	.img-wrap,
	.img {
		height: 100%;
		position: absolute !important;
		width: 100%;
	}
`;

const textPosition = props => {
	if ( 'right' === props.position ) {
		return css`
			${mq.md(
		css`
					right: 7%;
				`
	)};
			${mq.xl(
		css`
					right: 9%;
				`
	)};
			${mq.xxl(
		css`
					right: 12%;
				`
	)};
		`;
	} else if ( 'left' === props.position ) {
		return css`
			${mq.md(
		css`
					left: 7%;
				`
	)};
			${mq.xl(
		css`
					left: 9%;
				`
	)};
			${mq.xxl(
		css`
					left: 15%;
				`
	)};
		`;
	} else if ( 'center' === props.position ) {
		return css`
			${mq.md(
		css`
					left: 50% !important;
					transform: translateX(-50%) !important;
					`
	)};
		`;
	}
};

const StyledContentContainer = styled( 'div' )`
	background: ${colors.white};
	margin: -40px auto 0;
	padding: 20px 30px;
	position: relative;
	width: 84%;
	${textPosition}

	${mq.md( css`
		background: none;
		color: ${colors.white};
		margin: 0;
		padding: 0;
		position: absolute;
		top: 50%;
		transform: translateY(-50%);
		width: 42%;
	` )};

	${mq.lg( css`
		max-width: 525px;
		width: 50%;
	` )};

	${mq.xl( css`
		max-width: 570px;
		transform: translateY(-55%);
	` )};
`;

const StyledOverlayContentContainer = styled( 'div' )`
	
	background: none;	
	position: absolute;
	margin-top: -40px;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	width: 70%;

	${mq.md( css`
		width: 50%;
		margin-top: -10px;
	` )};

	${mq.lg( css`
		max-width: 525px;
		width: 50%;
		margin-top: -30px;
	` )};

	${mq.xl( css`
		max-width: 570px;
		transform: translate(-50%, -50%);
		margin-top: -30px;
	` )};

	.gatsby-image-wrapper {
		background-color: transparent;
	}
`;

const StyledSpacer = styled( 'div' )`
	height: 0;
	padding-bottom: 420px;
	position: relative;

	${mq.md(
		css`
		padding-bottom: 670px;
	`
	)};
	${mq.xl(
		css`
		padding-bottom: 750px;
	`
	)};
`;

//  ==========================================================================
//  ImageBreaker Component
//  ==========================================================================
const ImageBreakerComponent = ( {
	image,
	imageAlt,
	children,
} ) => {

	return (
		<StyledImageBreaker>
			<ImageWrapper>
				<Image
					alt={imageAlt}
					sizes={image}
					attachment={image}
					artDirectedSizes="(min-width: 768px) 100vw, 250vw"
					style={{ position: 'absolute' }}
					outerWrapperClassName="img-wrap"
					className="img"
				/>
			</ImageWrapper>
			{children}
		</StyledImageBreaker>
	);
};

ImageBreakerComponent.propTypes = {
	image: PropTypes.object.isRequired,
	imageAlt: PropTypes.string,
};

ImageBreakerComponent.defaultProps = {
	imageAlt: '',
};

export const ImageBreaker = ImageBreakerComponent;

//  ==========================================================================
//  VideoBreaker Component
//  ==========================================================================

const VideoBreakerComponent = ( {
	video,
	mobileVideo,
	poster,
	posterAlt,
	children
} ) => {

	const [windowWidth, setWindowWidth] = useState(
		'undefined' !== typeof window ? window.innerWidth : null
	);

	useEffect( () => {
		if ( windowWidth && mobileVideo ) {
			const onResize = () => {
				setWindowWidth( window.innerWidth );
			};

			window.addEventListener( 'resize', debounce( onResize ) );

			return () => {
				window.removeEventListener( 'resize', onResize );
			};
		}
	}, [] );

	// if ( !windowWidth ) {
	// 	return (
	// 		<ImageBreakerComponent
	// 			image={poster}
	// 			imageAlt={posterAlt}
	// 		>
	// 			{children}
	// 		</ImageBreakerComponent>
	// 	);
	// }

	return (
		<StyledVideoBreaker>
			<StyledSpacer>
				<StyledVideo 
					muted
					loop
					autoPlay
					playsInline
					poster={poster.src}
				>	
					{720 > windowWidth && mobileVideo ? (
						<source src={mobileVideo} type="video/mp4" />
					) : (
						<source src={video} type="video/mp4" />
					)}
				</StyledVideo>
			</StyledSpacer>
			
			{children}
		</StyledVideoBreaker>
	);
};

VideoBreakerComponent.propTypes = {
	video: PropTypes.string.isRequired,
	poster: PropTypes.oneOf( [
		PropTypes.object,
	] ).isRequired,
	posterAlt: PropTypes.string.isRequired
};

export const VideoBreaker = VideoBreakerComponent;

//  ==========================================================================
//  ImageBreakerContent Component
//  ==========================================================================
export const ImageBreakerContentComponent = ( { children, position } ) => {
	return(
		<StyledContentContainer
			className="image-breaker-content-component"
			position={position}
		>
			{children}
		</StyledContentContainer>
	);
};

ImageBreakerContentComponent.propTypes = {
	position: PropTypes.oneOf( ['right', 'center', 'left'] ),
};

ImageBreakerContentComponent.defaultProps = {
	position: 'left',
};

export const ImageBreakerContent = ImageBreakerContentComponent;

//  ==========================================================================
//  ImageBreakerContent Component
//  ==========================================================================
export const ImageBreakerOverlayContentComponent = ( { children, position } ) => {
	return(
		<StyledOverlayContentContainer
			className="breaker-overlay-content-component"
			position={position}
		>
			{children}
		</StyledOverlayContentContainer>
	);
};

ImageBreakerOverlayContentComponent.propTypes = {
	position: PropTypes.oneOf( ['right', 'center', 'left'] ),
};

ImageBreakerOverlayContentComponent.defaultProps = {
	position: 'center',
};

export const ImageBreakerOverlayContent = ImageBreakerOverlayContentComponent;
